import type { Coding } from '@medplum/fhirtypes'
import { OBSERVATION_INTERPRETATION_SYSTEM } from '#src/codeSystems.ts'
import { BODY_MASS_INDEX_CODING, loincCodingOf } from '#src/models/loinc.ts'
import type { ScoreBand } from '#src/observation-logic/derivations/scores.ts'
import { nutritionCoding } from '#src/terminology/nutrition.ts'
import {
  ORTHOSTATIC_CONSENSUS_SOURCE,
  ORTHOSTATIC_DIASTOLIC_CHANGE_CODING,
  ORTHOSTATIC_HEART_RATE_CHANGE_CODING,
  ORTHOSTATIC_SYSTOLIC_CHANGE_CODING,
} from '#src/terminology/vitals.ts'

// Where a set of cutoffs comes from, shown beside the value it bands.
export interface BandSource {
  citation: string
  url?: string
}

// One published set of cutoffs for a code. A note picks among a code's sets
// by id.
export interface BandSet {
  id: string
  display: string
  bands: readonly ScoreBand[]
  source?: BandSource
}

// The interpretation band sets of one observation code.
export interface CodedBands {
  coding: Coding
  bandSets: readonly BandSet[]
  // The set a note uses until it picks one; absent means the first.
  defaultId?: string
}

function v3(code: string, display: string): Coding {
  return { system: OBSERVATION_INTERPRETATION_SYSTEM, code, display }
}

// BMI cutoffs for Asian American adults, in kg/m² to one decimal: the ones
// the Estimated Nutritional Needs tool has shown dietitians, with its
// reference. LOINC carries no reference ranges for 39156-5.
export const BMI_ASIAN_AMERICAN_BANDS: readonly ScoreBand[] = [
  {
    max: 18.4,
    code: v3('L', 'Low'),
    display: 'below healthy range',
    severity: 'yellow',
  },
  {
    min: 18.5,
    max: 22.9,
    code: v3('N', 'Normal'),
    display: 'within healthy range',
    severity: 'green',
  },
  {
    min: 23,
    max: 26.9,
    code: v3('H', 'High'),
    display: 'above healthy range',
    severity: 'yellow',
  },
  {
    min: 27,
    code: v3('H', 'High'),
    display: 'further above healthy range',
    severity: 'orange',
  },
]

// Adult BMI categories (WHO), in kg/m² to one decimal.
export const BMI_WHO_BANDS: readonly ScoreBand[] = [
  {
    max: 18.4,
    code: v3('L', 'Low'),
    display: 'underweight',
    severity: 'yellow',
  },
  {
    min: 18.5,
    max: 24.9,
    code: v3('N', 'Normal'),
    display: 'normal',
    severity: 'green',
  },
  {
    min: 25,
    max: 29.9,
    code: v3('H', 'High'),
    display: 'overweight',
    severity: 'yellow',
  },
  { min: 30, code: v3('H', 'High'), display: 'obese', severity: 'orange' },
]

export const BMI_BAND_SETS: readonly BandSet[] = [
  {
    id: 'bmi-asian-american',
    display: 'Asian American adults',
    bands: BMI_ASIAN_AMERICAN_BANDS,
    source: {
      citation:
        'Li Z, Daniel S, Fujioka K, Umashanker D. Obesity among Asian American people in the United States: a review. Obesity 2023;31(2):316–28.',
      url: 'https://doi.org/10.1002/oby.23639',
    },
  },
  {
    id: 'bmi-who',
    display: 'WHO adults',
    bands: BMI_WHO_BANDS,
    source: {
      citation:
        'World Health Organization. Obesity: preventing and managing the global epidemic. WHO Technical Report Series 894; 2000.',
      url: 'https://pubmed.ncbi.nlm.nih.gov/11234459/',
    },
  },
]

const MALNUTRITION_WEIGHT_LOSS_SOURCE: BandSource = {
  citation:
    'White JV, Guenter P, Jensen G, et al. Consensus statement of the Academy of Nutrition and Dietetics/American Society for Parenteral and Enteral Nutrition: characteristics recommended for the identification and documentation of adult malnutrition (undernutrition). J Acad Nutr Diet 2012;112(5):730–8.',
  url: 'https://doi.org/10.1016/j.jand.2012.03.012',
}

// A loss over the interval is significant past its threshold, as the RD
// templates list them: more than 2% in a week, 5% in a month and 7.5% in
// three months; 10% in six months and 20% in a year. The value is signed,
// a loss negative, to one decimal.
function weightLossBandSet(
  id: string,
  display: string,
  significantAtOrBelow: number
): BandSet {
  return {
    id,
    display,
    source: MALNUTRITION_WEIGHT_LOSS_SOURCE,
    bands: [
      {
        max: significantAtOrBelow,
        code: v3('A', 'Abnormal'),
        display: 'significant loss',
        severity: 'orange',
      },
      {
        min: Math.round((significantAtOrBelow + 0.1) * 10) / 10,
        code: v3('N', 'Normal'),
        display: 'not a significant loss',
        severity: 'green',
      },
    ],
  }
}

export const WEIGHT_CHANGE_BAND_SETS: readonly BandSet[] = [
  weightLossBandSet('weight-loss-1-week', 'Over 1 week (>2%)', -2.1),
  weightLossBandSet('weight-loss-1-month', 'Over 1 month (>5%)', -5.1),
  weightLossBandSet('weight-loss-3-months', 'Over 3 months (>7.5%)', -7.6),
  weightLossBandSet('weight-loss-6-months', 'Over 6 months (≥10%)', -10),
  weightLossBandSet('weight-loss-1-year', 'Over 1 year (≥20%)', -20),
]

// The consensus thresholds for the change on standing, in whole mmHg and
// beats per minute: a drop of 20 systolic or 10 diastolic is orthostatic
// hypotension; a rise of 30 in heart rate is the tachycardia the postural
// tachycardia syndrome is defined by, when there is no orthostatic
// hypotension.
function orthostaticBandSet(
  id: string,
  bands: readonly ScoreBand[]
): readonly BandSet[] {
  return [
    {
      id,
      display: 'Consensus definition (2011)',
      bands,
      source: ORTHOSTATIC_CONSENSUS_SOURCE,
    },
  ]
}

export const ORTHOSTATIC_SYSTOLIC_BANDS: readonly ScoreBand[] = [
  {
    max: -20,
    code: v3('A', 'Abnormal'),
    display: 'drop of 20 or more',
    severity: 'orange',
  },
  {
    min: -19,
    code: v3('N', 'Normal'),
    display: 'no significant drop',
    severity: 'green',
  },
]

export const ORTHOSTATIC_DIASTOLIC_BANDS: readonly ScoreBand[] = [
  {
    max: -10,
    code: v3('A', 'Abnormal'),
    display: 'drop of 10 or more',
    severity: 'orange',
  },
  {
    min: -9,
    code: v3('N', 'Normal'),
    display: 'no significant drop',
    severity: 'green',
  },
]

export const ORTHOSTATIC_HEART_RATE_BANDS: readonly ScoreBand[] = [
  {
    max: 29,
    code: v3('N', 'Normal'),
    display: 'rise under 30',
    severity: 'green',
  },
  {
    min: 30,
    code: v3('A', 'Abnormal'),
    display: 'rise of 30 or more',
    severity: 'yellow',
  },
]

// Band sets for observation codes outside an instrument, in the shape
// instrument score bands use. This table is where a server-backed range
// would plug in: an org-defined ValueSet or CodeSystem of bands, or the
// Observation.referenceRange a lab reports.
export const OBSERVATION_BANDS: readonly CodedBands[] = [
  { coding: loincCodingOf(BODY_MASS_INDEX_CODING), bandSets: BMI_BAND_SETS },
  {
    coding: nutritionCoding('percent-weight-change-from-usual'),
    bandSets: WEIGHT_CHANGE_BAND_SETS,
    defaultId: 'weight-loss-1-month',
  },
  {
    coding: ORTHOSTATIC_SYSTOLIC_CHANGE_CODING,
    bandSets: orthostaticBandSet(
      'orthostatic-systolic-consensus',
      ORTHOSTATIC_SYSTOLIC_BANDS
    ),
  },
  {
    coding: ORTHOSTATIC_DIASTOLIC_CHANGE_CODING,
    bandSets: orthostaticBandSet(
      'orthostatic-diastolic-consensus',
      ORTHOSTATIC_DIASTOLIC_BANDS
    ),
  },
  {
    coding: ORTHOSTATIC_HEART_RATE_CHANGE_CODING,
    bandSets: orthostaticBandSet(
      'orthostatic-heart-rate-consensus',
      ORTHOSTATIC_HEART_RATE_BANDS
    ),
  },
]
